import {User, UserManager, WebStorageStateStore} from 'oidc-client-ts';
import {oidcSettings} from '@/common/auth/oidcConfig.js';

/**
 * OIDC — schlanker Wrapper um den `oidc-client-ts`-`UserManager` (Authorization Code +
 * PKCE, Silent-Renew). Kapselt Login-Redirect, Callback, Token-Zugriff und Logout,
 * damit {@link useAuth} nur eine flache API sieht. Der user-switch läuft weiterhin über
 * die REST-Endpunkte `/user/switch*` (die jetzt kurzlebige OIDC-Tokens liefern) — das
 * Admin-OIDC-Token bleibt im oidc-client-ts-Store und dient dem Revert.
 *
 * Die App-Sprache wird als `ui_locales` beim Login übergeben → der server-gerenderte
 * API-Login erscheint in derselben Sprache.
 */

let manager: UserManager | null = null;

function userManager(): UserManager {
  if (!manager) {
    manager = new UserManager({
      authority: oidcSettings.authority,
      client_id: oidcSettings.client_id,
      redirect_uri: oidcSettings.redirect_uri,
      post_logout_redirect_uri: oidcSettings.post_logout_redirect_uri,
      response_type: oidcSettings.response_type,
      scope: oidcSettings.scope,
      resource: oidcSettings.resource,
      automaticSilentRenew: true,
      // Tokens in localStorage → überleben einen Reload (Silent-Renew im Hintergrund).
      userStore: new WebStorageStateStore({store: window.localStorage}),
    });
  }
  return manager;
}

export const oidcClient = {
  /** Startet den Login (Full-Page-Redirect auf den API-Login); `uiLocales` = App-Sprache. */
  login(uiLocales?: string): Promise<void> {
    return userManager().signinRedirect({
      extraQueryParams: {resource: oidcSettings.resource, ...(uiLocales ? {ui_locales: uiLocales} : {})},
    });
  },

  /** Verarbeitet den Rückkanal nach dem Login (Code→Token). */
  handleCallback(): Promise<User> {
    return userManager().signinRedirectCallback();
  },

  /** Gespeicherter (ggf. abgelaufener) User oder null. */
  getUser(): Promise<User | null> {
    return userManager().getUser();
  },

  /** Access-Token eines gültigen (nicht abgelaufenen) Users, sonst null. */
  async getValidAccessToken(): Promise<string | null> {
    const user = await userManager().getUser();
    return user && !user.expired ? user.access_token : null;
  },

  /** Silent-Renew; liefert das neue Access-Token oder null bei Fehlschlag. */
  async renew(): Promise<string | null> {
    // Ohne gespeicherte Session KEIN Silent-Renew: `signinSilent` würde sonst einen
    // prompt=none-Iframe auf die Redirect-URI laden → Iframe-Kaskade/Loop.
    if (!(await userManager().getUser())) {
      return null;
    }
    try {
      const user = await userManager().signinSilent();
      return user?.access_token ?? null;
    } catch {
      return null;
    }
  },

  /** RP-initiated Logout (Full-Page-Redirect zum OP-Logout). */
  logout(): Promise<void> {
    return userManager().signoutRedirect();
  },

  /** Nur lokalen Token-Zustand verwerfen (ohne Redirect). */
  removeUser(): Promise<void> {
    return userManager().removeUser();
  },
};
